import { DoneButton } from "components/blue-button";
import { ClassListInput } from "components/classrooms/class-list-input";
import DismissButton from "components/dismiss-button";
import { Selector } from "components/selector";
import UploadBox from "components/upload-box";
import styles from 'css/components/classrooms/create-class-overlay.module.css'
import React, { useRef, useState } from "react";

import { useCreateClassMutation } from "../../util/rtk-api";
import { useNavigate } from "react-router-dom";
import { ProgressCircle } from "components/progress-circle";
import { showToast } from "components/toast";

import * as XLSX from 'xlsx'
// TODO: Update when screens are finalized
export const CreateClassroomModal = ({ setShowCreateClassroomModal }) => {
    const [subjects, setSubjects] = useState([
        'Science', 'Math', 'ELA',
        'Social Studies', 'Other',
    ])
    const [students, setStudents] = useState([])
    const [formData, setFormData] = useState({
        name: '',
        grades: [],
        period: '0',
        subject: '',
        defaultPassword: ''
    })
    const [createClassroom, { isLoading }] = useCreateClassMutation()
    const navigate = useNavigate()
    // Idempotency key (L1): stable for the life of this modal, so a double-click
    // or a retry after a timeout can't provision a second set of student accounts
    const requestIdRef = useRef<string>(
        (typeof crypto !== 'undefined' && crypto.randomUUID)
            ? crypto.randomUUID()
            : `req-${Date.now()}-${Math.random().toString(36).slice(2)}`
    )
    // TODO: Fetch subjects from API
    // useEffect(() => {
    //     const subjects = getUserDetails().subjectsTaught
    //     setSubjects(subjects)
    // }, [])

    const handleParseClassList = async (file: File) => {
        const expectedHeaders = ['schoolstudentid', 'lastname', 'firstname', 'grade', 'difficulty']

        const parseRowsToStudents = (rows: any[][]) => {
            rows = rows.slice(3) // Removes instructions
            const nonEmptyRows = rows.filter(r => Array.isArray(r) && r.some(cell => String(cell ?? '').trim() !== ''))
            if (nonEmptyRows.length === 0) return []

            const firstRow = nonEmptyRows[0].map((h: any) => String(h ?? '').trim().toLowerCase().replace(/\s+/g, ''))
            const isHeader = expectedHeaders.some(h => firstRow.includes(h))

            let startIndex = 0
            let columnIndexByHeader: Record<string, number> | null = null
            if (isHeader) {
                startIndex = 1
                columnIndexByHeader = {}
                expectedHeaders.forEach(h => {
                    const idx = firstRow.indexOf(h)
                    if (idx >= 0) columnIndexByHeader![h] = idx
                })
            }

            const dataRows = nonEmptyRows.slice(startIndex)
            return dataRows.map((row) => {
                let schoolStudentId = ''
                let lastName = ''
                let firstName = ''
                let grade = ''
                let difficulty = ''

                if (columnIndexByHeader) {
                    schoolStudentId = String(row[columnIndexByHeader['schoolstudentid']] ?? '').trim()
                    lastName = String(row[columnIndexByHeader['lastname']] ?? '').trim()
                    firstName = String(row[columnIndexByHeader['firstname']] ?? '').trim()
                    grade = String(row[columnIndexByHeader['grade']] ?? '').trim()
                    difficulty = String(row[columnIndexByHeader['difficulty']] ?? '').trim()
                } else {
                    // Assume canonical order: schoolStudentId, lastName, firstName, grade, difficulty
                    schoolStudentId = String(row[0] ?? '').trim()
                    lastName = String(row[1] ?? '').trim()
                    firstName = String(row[2] ?? '').trim()
                    grade = String(row[3] ?? '').trim()
                    difficulty = String(row[4] ?? '').trim()
                }

                return {
                    schoolStudentId: schoolStudentId || '',
                    firstName: firstName || '',
                    lastName: lastName || '',
                    difficulty: difficulty || 'Not Assigned',
                    grade: grade || ''
                }
            })
        }

        if (file.name.endsWith('.csv')) {
            const text = await file.text()
            const lines = text.split(/\r?\n/).filter(line => line.trim())
            const rows = lines.map(line => line.split(',').map(field => field.trim()))
            const parsedStudents = parseRowsToStudents(rows as any[][])
            setStudents(parsedStudents)
            return
        }

        if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
            const arrayBuffer: ArrayBuffer = await new Promise((resolve, reject) => {
                const reader = new FileReader()
                reader.onload = (e) => resolve(e.target?.result as ArrayBuffer)
                reader.onerror = reject
                reader.readAsArrayBuffer(file)
            })
            const workbook = XLSX.read(arrayBuffer, { type: 'array' })
            const sheetName = workbook.SheetNames[0]
            const worksheet = workbook.Sheets[sheetName]
            const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' }) as any[][]
            const parsedStudents = parseRowsToStudents(rows)
            setStudents(parsedStudents)
            return
        }

        throw new Error('Unsupported file format')
    }

    const handleCreateClassroom = async (e?: React.FormEvent) => {
        if (e) {
            e.preventDefault()
        }

        // Was fire-and-forget: on failure `data` was undefined and we navigated
        // to /classrooms/undefined. Await + surface the error instead.
        try {
            const data = await createClassroom({
                ...formData,
                students: students,
                requestId: requestIdRef.current,
            }).unwrap()
            navigate(`/classrooms/${data.classId}`)
        } catch (err) {
            showToast("We couldn't create this class. Please check your connection and try again.")
        }
    }

    const onDefaultPasswordBlur = ({ target }) => {
        const conditions = [
            /.{8,}/,
            /[0-9]+/,
            /[a-z]+/,
            /[A-Z]+/,
            /[\^$*.[\]{}()?\-"!@#%&/\\,><':;|_~`+=]+/,
        ]

        setFormData(prev => ({ ...prev, defaultPassword: target.value }))

        if (conditions.every(pattern => target.value.match(pattern))) {
            target.setCustomValidity('')
            return
        }

        target.setCustomValidity(
            'The password must be at least 8 characters long and must use a lower case character, an upper case character, a number, and a special character.'
        )
    }

    return (
        <div className="overlay">
            <div className="overlay-content-container">
                <div className="overlay-content-header">
                    <h3>Create New </h3>
                    <DismissButton onClick={() => setShowCreateClassroomModal(false)} />
                </div>
                {isLoading ? <div className="overlay-content-body">
                    <div className="overlay-content-body-item">
                        <div className="overlay-content-body-item-label">
                            Creating Classroom...
                            <ProgressCircle />
                        </div>
                    </div>
                </div> :
                    <form className={`overlay-content-body ${styles.overlayContentBody}`} onSubmit={handleCreateClassroom}>
                        <div className={styles.classroomFormRow}>
                            <label className={styles.noWrap} htmlFor="classroom-name">Classroom Name: </label>
                            <input
                                id="classroom-name"
                                type="text"
                                maxLength={50}
                                value={formData.name}
                                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="Max 50 characters. E.g. Social Studies 9, AP Chemistry, Third Grade"
                                required
                            />
                        </div>
                        <div className={`${styles.flexColumn}`}>
                            <label htmlFor="grade">Grade:
                                <div className={styles.gray}>(Select all that apply)</div>
                            </label>
                            <div className={styles.checkboxGroup}>
                                <label>
                                    <input
                                        type="checkbox"
                                        name="grade"
                                        value="k"
                                        checked={formData.grades?.includes('k')}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setFormData(prev => ({
                                                ...prev,
                                                grades: e.target.checked
                                                    ? [...(prev.grades || []), value]
                                                    : (prev.grades || []).filter(g => g !== value)
                                            }))
                                        }}
                                    />
                                    Kindergarten
                                </label>
                                {[...Array(12)].map((_, i) => (
                                    <label key={i}>
                                        <input
                                            type="checkbox"
                                            name="grade"
                                            value={String(i + 1)}
                                            checked={formData.grades?.includes(String(i + 1))}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setFormData(prev => ({
                                                    ...prev,
                                                    grades: e.target.checked
                                                        ? [...(prev.grades || []), value]
                                                        : (prev.grades || []).filter(g => g !== value)
                                                }))
                                            }}
                                        />
                                        Grade {i + 1}
                                    </label>
                                ))}
                                <label>
                                    <input
                                        type="checkbox"
                                        name="grade"
                                        value="transition"
                                        checked={formData.grades?.includes('transition')}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setFormData(prev => ({
                                                ...prev,
                                                grades: e.target.checked
                                                    ? [...(prev.grades || []), value]
                                                    : (prev.grades || []).filter(g => g !== value)
                                            }))
                                        }}
                                    />
                                    Transition(18+)
                                </label>
                            </div>
                        </div>
                        <div className={styles.classroomFormRow}>
                            <Selector
                                options={['All Day', '1', '2', '3', '4', '5', '6', '7', '8']}
                                onSelect={(value) => setFormData(prev => ({ ...prev, period: value }))}
                                style={styles.width325}
                                placeholder="Period"
                                required
                                value={formData.period}
                            />
                            <Selector
                                options={subjects}
                                onSelect={(value) => setFormData(prev => ({ ...prev, subject: value }))}
                                style={styles.width325}
                                placeholder="Subject"
                                required
                                value={formData.subject}
                            />
                        </div>

                        <div className={styles.classroomFormRow}>
                            <label className={styles.noWrap} htmlFor="default-password">Default Password: </label>
                            <input
                                id="default-password"
                                className={styles.defaultPassword}
                                type="text"
                                value={formData.defaultPassword}
                                onChange={(e) => setFormData(prev => ({ ...prev, defaultPassword: e.target.value }))}
                                placeholder="Minimum 8 characters, include uppercase, lowercase, number, and symbol"
                                onBlur={onDefaultPasswordBlur}
                                required
                            />
                        </div>

                        <div className={styles.flexColumn}>
                            Class list: <br></br>
                            <div className={styles.bulkUploadText}>
                                CLASS UPLOAD: You can upload an accepted file as long as it has one record per row. Ensure that your spreadsheet columns match template provided below.
                            </div>
                            <a href="https://backend-artifacts-490004643038.s3.us-east-1.amazonaws.com/Classroom_Template.xlsx" target="_blank" rel="noopener noreferrer" download>
                                Download class list template (.xlsx)
                            </a>
                        </div>
                        <div className="upload-box-row">
                            <UploadBox
                                name="Upload Class List"
                                acceptedFileTypes={[
                                    'text/csv',
                                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                                    'application/vnd.ms-excel'
                                ]}
                                acceptedFileExtensionsText=".CSV, .XLSX, .XLS"
                                onFileUpload={handleParseClassList}
                            />
                        </div>
                        {/* Or manually
                     enter your class list here: */}
                        {/* <ClassListInput /> */}
                        <div className={styles.alignRight}>
                            <DoneButton disabled={isLoading} type="submit" />
                        </div>
                    </form>
                }
            </div>
        </div>
    )
}